import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import ProPortalHeader from '@/components/ProPortalHeader';
import { wget, wpost, wput } from '@/components/website/api';
import StepBusiness from '@/components/website/steps/StepBusiness';
import StepBranding from '@/components/website/steps/StepBranding';
import StepServices from '@/components/website/steps/StepServices';
import StepImages from '@/components/website/steps/StepImages';
import { StepAbout, StepAreas, StepEnquiries, StepExtra, StepSocial } from '@/components/website/steps/SimpleSteps';
import { StepDomain, StepEmails, StepReviews } from '@/components/website/steps/ListSteps';
import { OrderSummary } from '@/components/website/OrderSummary';

export const STEPS = [
  { key: 'business', label: 'Business' }, { key: 'branding', label: 'Branding' }, { key: 'services', label: 'Services' },
  { key: 'areas', label: 'Areas' }, { key: 'about', label: 'About' }, { key: 'social', label: 'Social' }, { key: 'images', label: 'Photos' }, { key: 'reviews', label: 'Reviews' },
  { key: 'domain', label: 'Domain' }, { key: 'emails', label: 'Emails' }, { key: 'enquiries', label: 'Enquiries' }, { key: 'extra', label: 'Extra' }, { key: 'review', label: 'Review & Pay' },
];

export default function WebsiteWizard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState('idle'); // idle | saving | saved | error
  const [paying, setPaying] = useState(false);
  const [included, setIncluded] = useState(false);
  useEffect(() => { fetch(`${API_URL}/api/billing/me`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => setIncluded(!!(d?.tier?.features || d?.plan?.tier?.features || {}).website_build_included)).catch(() => {}); }, []);
  const pending = useRef({});
  const timer = useRef(null);

  useEffect(() => {
    wget('/website/me').then(async (d) => {
      const draft = d.draft || await wput('/website/draft', {});
      setOrder(draft);
      setStep(Math.min(draft.current_step || 0, STEPS.length - 1));
    }).catch((e) => toast.error(e.message));
    if (params.get('cancelled')) toast.message('Payment cancelled — your answers are saved, nothing was charged.');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const flush = useCallback(async () => {
    const body = pending.current;
    pending.current = {};
    if (!Object.keys(body).length) return;
    setSaving('saving');
    try { await wput('/website/draft', body); setSaving('saved'); } catch (e) { setSaving('error'); toast.error(`Could not save: ${e.message}`); }
  }, []);
  const queue = useCallback((patch) => {
    pending.current = { ...pending.current, ...patch, form: { ...(pending.current.form || {}), ...(patch.form || {}) } };
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 900);
  }, [flush]);
  useEffect(() => () => { clearTimeout(timer.current); flush(); }, [flush]);

  const setSection = (key) => (value) => {
    setOrder((o) => {
      const next = typeof value === 'function' ? value((o.form || {})[key]) : value;
      queue({ form: { [key]: next } });
      return { ...o, form: { ...(o.form || {}), [key]: next } };
    });
  };
  const goTo = async (i) => { await flush(); setStep(i); queue({ current_step: i }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const pay = async () => {
    await flush();
    setPaying(true);
    try {
      const d = await wpost('/website/checkout', { origin_url: window.location.origin });
      if (d.included) { toast.success(`Website order ${d.order?.reference || ''} submitted${d.offline ? ' — already paid, nothing more to do.' : " — it's included in your package."}`); navigate('/my-website'); return; }
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(e.message); setPaying(false); }
  };

  if (!order) return <div className="min-h-screen bg-[#E8ECEF]"><ProPortalHeader /><div className="p-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></div>;
  const f = order.form || {};
  const cur = STEPS[step];
  const body = {
    business: <StepBusiness data={f.business || {}} set={setSection('business')} />,
    branding: <StepBranding data={f.branding || {}} set={setSection('branding')} order={order} onFilesChange={setOrder} />,
    services: <StepServices data={f.services || {}} set={setSection('services')} />,
    areas: <StepAreas data={f.areas || {}} set={setSection('areas')} />,
    about: <StepAbout data={f.about || {}} set={setSection('about')} />,
    social: <StepSocial data={f.social || {}} set={setSection('social')} />,
    images: <StepImages order={order} onFilesChange={setOrder} />,
    reviews: <StepReviews data={f.reviews || []} set={setSection('reviews')} />,
    domain: <StepDomain data={f.domain || {}} set={setSection('domain')} />,
    emails: <StepEmails data={f.emails || []} set={setSection('emails')} />,
    enquiries: <StepEnquiries data={f.enquiries || {}} set={setSection('enquiries')} />,
    extra: <StepExtra data={f.extra || {}} set={setSection('extra')} />,
    review: <ReviewStep order={order} onEdit={goTo} onPay={pay} paying={paying} included={included || !!order.payment?.offline} />,
  }[cur.key];

  return (
    <div className="min-h-screen bg-[#E8ECEF]" data-testid="website-wizard">
      <ProPortalHeader />
      <main className="max-w-3xl mx-auto px-4 py-5 md:py-8 pb-28">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => navigate('/my-website')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="ww-back-home"><ArrowLeft className="w-4 h-4" /> My Website</button>
          <span className="text-xs text-slate-500" data-testid="ww-save-state">{saving === 'saving' ? 'Saving…' : saving === 'saved' ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Saved</span> : saving === 'error' ? <span className="text-rose-600">Not saved</span> : 'Autosaves as you go'}</span>
        </div>
        <ol className="flex gap-1 overflow-x-auto pb-2 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="ww-steps">
          {STEPS.map((s, i) => (
            <li key={s.key}><button type="button" onClick={() => goTo(i)} data-testid={`ww-step-${s.key}`} aria-current={i === step || undefined}
              className={`whitespace-nowrap text-[11px] font-semibold px-2.5 py-1 rounded-full border ${i === step ? 'bg-slate-900 text-white border-slate-900' : i < step ? 'bg-teal-50 text-teal-800 border-teal-200' : 'bg-white text-slate-500 border-slate-200'}`}>{i + 1}. {s.label}</button></li>
          ))}
        </ol>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 md:p-6">{body}</div>
      </main>
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 p-3" data-testid="ww-nav">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => goTo(step - 1)} disabled={step === 0} data-testid="ww-prev" className="h-11"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <span className="text-xs text-slate-500">Step {step + 1} of {STEPS.length}</span>
          {step < STEPS.length - 1 ? <Button type="button" onClick={() => goTo(step + 1)} data-testid="ww-next" className="h-11 bg-slate-900 text-white">Next <ArrowRight className="w-4 h-4 ml-1" /></Button> : <span className="w-24" />}
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ order, onEdit, onPay, paying, included }) {
  return (
    <div className="space-y-5">
      <div><h2 className="text-xl md:text-2xl font-bold text-slate-900" data-testid="ww-step-title">Review &amp; submit</h2><p className="text-sm text-slate-600 mt-1">Check everything below — tap Edit on any section to change it.</p></div>
      <OrderSummary order={order} onEdit={onEdit} />
      <div className="rounded-2xl border-2 border-teal-600 bg-teal-50 p-5" data-testid="ww-pay-box">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-lg bg-teal-600 text-white flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5" /></span>
          <div className="flex-1">
            <h3 className="font-bold text-slate-900">{included ? 'Included in your package' : 'Web + CRM package'}</h3>
            {included ? <p className="text-sm text-slate-700 mt-1" data-testid="ww-included-note">Your website build, hosting, domain and email are all part of your package — nothing more to pay today.</p>
              : <ul className="text-sm text-slate-700 mt-1 space-y-0.5"><li><strong>£200</strong> one-off setup — design, build, domain &amp; email setup</li><li><strong>£50 / month</strong> — hosting, domain renewal, email, support and every Web + CRM feature in the portal</li></ul>}
            {!included && <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Secure card payment by Stripe. Prices exclude VAT where applicable.</p>}
          </div>
        </div>
        <Button type="button" onClick={onPay} disabled={paying} data-testid="ww-submit-order" className="w-full h-12 mt-4 bg-teal-700 hover:bg-teal-800 text-white text-base font-bold">{paying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {included ? 'Submitting…' : 'Opening secure checkout…'}</> : included ? 'Submit Website Order' : 'Join Web + CRM & Submit — £200 today'}</Button>
      </div>
    </div>
  );
}
